import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import ChatWidget from '../../home/components/ChatWidget';

interface Product {
  id: number;
  name: string;
  brand: string;
  type: 'laptop' | 'desktop';
  processor: string;
  processorType: 'intel' | 'amd' | 'apple';
  ram: string;
  ramSize: '8gb' | '16gb' | '32gb' | '64gb';
  storage: string;
  storageType: 'ssd' | 'hdd' | 'hybrid';
  storageSize: '256gb' | '512gb' | '1tb' | '2tb';
  display: string;
  image: string;
  rating: number;
  reviewCount: number;
  stock: 'in-stock' | 'low-stock' | 'out-of-stock';
  badge?: string;
}

const LaptopsDesktopsPage = () => {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');
  const prefix = isNL ? '/nl' : '';

  const [products, setProducts] = useState<Product[]>([
    {
      id: 1,
      name: 'Dell XPS 13 Ultrabook',
      brand: 'dell',
      type: 'laptop',
      processor: 'Intel Core i7-1365U',
      processorType: 'intel',
      ram: '16GB DDR4',
      ramSize: '16gb',
      storage: '512GB SSD',
      storageType: 'ssd',
      storageSize: '512gb',
      display: isNL ? '13,4" FHD+ Scherm' : '13.4" FHD+ Display',
      image: 'https://public.readdy.ai/ai/img_res/1afca2fac4dc2544d9d171255e768568.jpg',
      rating: 4.5,
      reviewCount: 127,
      stock: 'in-stock'
    },
    {
      id: 2,
      name: 'HP EliteDesk 800 G9',
      brand: 'hp',
      type: 'desktop',
      processor: 'Intel Core i7-12700',
      processorType: 'intel',
      ram: '32GB DDR4',
      ramSize: '32gb',
      storage: '1TB SSD',
      storageType: 'ssd',
      storageSize: '1tb',
      display: 'Intel UHD Graphics',
      image: 'https://readdy.ai/api/search-image?query=HP%20EliteDesk%20small%20form%20factor%20desktop%20computer%20black%20sleek%20design%20business%20workstation%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=hp-elitedesk&orientation=landscape',
      rating: 4.0,
      reviewCount: 89,
      stock: 'in-stock'
    },
    {
      id: 3,
      name: 'Lenovo ThinkPad E15',
      brand: 'lenovo',
      type: 'laptop',
      processor: 'AMD Ryzen 7 5700U',
      processorType: 'amd',
      ram: '16GB DDR4',
      ramSize: '16gb',
      storage: '512GB SSD',
      storageType: 'ssd',
      storageSize: '512gb',
      display: isNL ? '15,6" FHD Scherm' : '15.6" FHD Display',
      image: 'https://readdy.ai/api/search-image?query=Lenovo%20ThinkPad%20business%20laptop%20black%20professional%20design%20with%20red%20trackpoint%20button%20classic%20business%20laptop%20on%20clean%20white%20background&width=400&height=300&seq=lenovo-thinkpad&orientation=landscape',
      rating: 5.0,
      reviewCount: 203,
      stock: 'low-stock'
    },
    {
      id: 4,
      name: 'MacBook Pro 14" M3',
      brand: 'apple',
      type: 'laptop',
      processor: 'Apple M3 Chip',
      processorType: 'apple',
      ram: '16GB Unified Memory',
      ramSize: '16gb',
      storage: '512GB SSD',
      storageType: 'ssd',
      storageSize: '512gb',
      display: '14,2" Liquid Retina XDR',
      image: 'https://readdy.ai/api/search-image?query=MacBook%20Pro%2014%20inch%20space%20gray%20aluminum%20unibody%20design%20premium%20Apple%20laptop%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=macbook-pro&orientation=landscape',
      rating: 4.5,
      reviewCount: 156,
      stock: 'in-stock'
    },
    {
      id: 5,
      name: 'ASUS ProArt Station D940MX',
      brand: 'asus',
      type: 'desktop',
      processor: 'Intel Core i7-12700K',
      processorType: 'intel',
      ram: '32GB DDR4',
      ramSize: '32gb',
      storage: '1TB SSD',
      storageType: 'ssd',
      storageSize: '1tb',
      display: 'NVIDIA RTX 4060',
      image: 'https://readdy.ai/api/search-image?query=ASUS%20ProArt%20workstation%20desktop%20computer%20tower%20black%20professional%20design%20for%20creative%20professionals%20on%20clean%20white%20background&width=400&height=300&seq=asus-proart&orientation=landscape',
      rating: 4.0,
      reviewCount: 74,
      stock: 'in-stock'
    },
    {
      id: 6,
      name: 'Acer Aspire 5 A515-58',
      brand: 'acer',
      type: 'laptop',
      processor: 'Intel Core i5-1335U',
      processorType: 'intel',
      ram: '8GB DDR4',
      ramSize: '8gb',
      storage: '256GB SSD',
      storageType: 'ssd',
      storageSize: '256gb',
      display: isNL ? '15,6" FHD Scherm' : '15.6" FHD Display',
      image: 'https://readdy.ai/api/search-image?query=Acer%20Aspire%20laptop%20silver%20design%20affordable%20business%20laptop%20with%20modern%20sleek%20profile%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=acer-aspire&orientation=landscape',
      rating: 4.0,
      reviewCount: 92,
      stock: 'in-stock'
    },
    {
      id: 7,
      name: 'Microsoft Surface Pro 9',
      brand: 'microsoft',
      type: 'laptop',
      processor: 'Intel Core i7-1255U',
      processorType: 'intel',
      ram: '16GB LPDDR5',
      ramSize: '16gb',
      storage: '256GB SSD',
      storageType: 'ssd',
      storageSize: '256gb',
      display: '13" PixelSense Flow',
      image: 'https://readdy.ai/api/search-image?query=Microsoft%20Surface%20Pro%20tablet%20laptop%20hybrid%20device%20with%20kickstand%20and%20type%20cover%20platinum%20color%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=surface-pro&orientation=landscape',
      rating: 4.5,
      reviewCount: 148,
      stock: 'in-stock'
    },
    {
      id: 8,
      name: 'Dell OptiPlex 7000 Micro',
      brand: 'dell',
      type: 'desktop',
      processor: 'Intel Core i5-12500T',
      processorType: 'intel',
      ram: '16GB DDR4',
      ramSize: '16gb',
      storage: '512GB SSD',
      storageType: 'ssd',
      storageSize: '512gb',
      display: 'Intel UHD Graphics',
      image: 'https://readdy.ai/api/search-image?query=Dell%20OptiPlex%20micro%20desktop%20computer%20ultra%20compact%20black%20design%20space-saving%20business%20computer%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=dell-optiplex&orientation=landscape',
      rating: 4.5,
      reviewCount: 112,
      stock: 'in-stock'
    }
  ]);

  const [filteredProducts, setFilteredProducts] = useState<Product[]>(products);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedProcessors, setSelectedProcessors] = useState<string[]>([]);
  const [selectedRam, setSelectedRam] = useState<string[]>([]);
  const [selectedStorageType, setSelectedStorageType] = useState<string[]>([]);
  const [selectedStorageSize, setSelectedStorageSize] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    let filtered = products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          product.brand.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesBrand = selectedBrands.length === 0 || selectedBrands.includes(product.brand);
      const matchesType = selectedTypes.length === 0 || selectedTypes.includes(product.type);
      const matchesProcessor = selectedProcessors.length === 0 || selectedProcessors.includes(product.processorType);
      const matchesRam = selectedRam.length === 0 || selectedRam.includes(product.ramSize);
      const matchesStorageType = selectedStorageType.length === 0 || selectedStorageType.includes(product.storageType);
      const matchesStorageSize = selectedStorageSize.length === 0 || selectedStorageSize.includes(product.storageSize);

      return matchesSearch && matchesBrand && matchesType && 
             matchesProcessor && matchesRam && matchesStorageType && matchesStorageSize;
    });

    switch (sortBy) {
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
    }

    setFilteredProducts(filtered);
  }, [products, searchTerm, selectedBrands, selectedTypes, selectedProcessors, 
      selectedRam, selectedStorageType, selectedStorageSize, sortBy]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedBrands([]);
    setSelectedTypes([]);
    setSelectedProcessors([]);
    setSelectedRam([]);
    setSelectedStorageType([]);
    setSelectedStorageSize([]);
  };

  const handleFilterChange = (filterType: string, value: string, checked: boolean) => {
    switch (filterType) {
      case 'brand':
        setSelectedBrands(prev => checked 
          ? [...prev, value] 
          : prev.filter(item => item !== value));
        break;
      case 'type':
        setSelectedTypes(prev => checked 
          ? [...prev, value] 
          : prev.filter(item => item !== value));
        break;
      case 'processor':
        setSelectedProcessors(prev => checked 
          ? [...prev, value] 
          : prev.filter(item => item !== value));
        break;
      case 'ram':
        setSelectedRam(prev => checked 
          ? [...prev, value] 
          : prev.filter(item => item !== value));
        break;
      case 'storage-type':
        setSelectedStorageType(prev => checked 
          ? [...prev, value] 
          : prev.filter(item => item !== value));
        break;
      case 'storage-size':
        setSelectedStorageSize(prev => checked 
          ? [...prev, value] 
          : prev.filter(item => item !== value));
        break;
    }
  };

  const getStockBadge = (stock: string) => {
    switch (stock) {
      case 'in-stock':
        return <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">{isNL ? 'Op Voorraad' : 'In Stock'}</span>;
      case 'low-stock':
        return <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">{isNL ? 'Beperkte Voorraad' : 'Low Stock'}</span>;
      case 'out-of-stock':
        return <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">{isNL ? 'Niet Op Voorraad' : 'Out of Stock'}</span>;
      default:
        return null;
    }
  };

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, i) => (
      <div key={i} className="w-4 h-4 flex items-center justify-center">
        <i className={`${i < Math.floor(rating) ? 'ri-star-fill' : i < rating ? 'ri-star-half-fill' : 'ri-star-line'} text-yellow-400`}></i>
      </div>
    ));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm">
            <a href={prefix || '/'} className="text-gray-600 hover:text-teal-600">Home</a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-gray-600">Hardware</span>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-gray-900 font-medium">{isNL ? 'Laptops & Desktops' : 'Laptops & Desktops'}</span>
          </nav>
        </div>
      </div>

      {/* Page Header */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{isNL ? 'Laptops & Desktops' : 'Laptops & Desktops'}</h1>
            <p className="text-lg text-gray-600 mb-6">
              {isNL
                ? 'Ontdek onze uitgebreide selectie van zakelijke laptops en desktopcomputers van toonaangevende fabrikanten. Perfect voor zakelijke omgevingen, thuiswerken en professionele toepassingen.'
                : 'Discover our comprehensive selection of enterprise-grade laptops and desktop computers from leading manufacturers. Perfect for business environments, remote work, and professional applications.'}
            </p>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">
                {isNL ? (
                  <><span className="font-medium">{filteredProducts.length}</span> producten weergegeven</>
                ) : (
                  <>Showing <span className="font-medium">{filteredProducts.length}</span> products</>
                )}
              </span>
              <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
              <span className="text-sm text-gray-500">{isNL ? 'Neem contact op voor prijzen' : 'Contact us for pricing'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <div className="flex gap-8">
            {/* Sidebar Filters */}
            <div className="w-80 flex-shrink-0">
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 sticky top-20">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold">{isNL ? 'Filters' : 'Filters'}</h3>
                  <button 
                    className="text-sm text-teal-600 hover:underline cursor-pointer"
                    onClick={clearAllFilters}
                  >
                    {isNL ? 'Alles Wissen' : 'Clear All'}
                  </button>
                </div>

                {/* Search */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{isNL ? 'Producten Zoeken' : 'Search Products'}</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={isNL ? 'Zoek op model, merk...' : 'Search by model, brand...'}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-search-line"></i>
                    </div>
                  </div>
                </div>

                {/* Brand Filter */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">{isNL ? 'Merk' : 'Brand'}</label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {['dell', 'hp', 'lenovo', 'apple', 'asus', 'acer', 'microsoft'].map(brand => (
                      <label key={brand} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          checked={selectedBrands.includes(brand)}
                          onChange={(e) => handleFilterChange('brand', brand, e.target.checked)}
                        />
                        <span className="ml-2 text-sm text-gray-600 capitalize">{brand}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Form Factor */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">{isNL ? 'Vormfactor' : 'Form Factor'}</label>
                  <div className="space-y-2">
                    {[
                      { value: 'laptop', label: 'Laptop' },
                      { value: 'desktop', label: 'Desktop' }
                    ].map(type => (
                      <label key={type.value} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          checked={selectedTypes.includes(type.value)}
                          onChange={(e) => handleFilterChange('type', type.value, e.target.checked)}
                        />
                        <span className="ml-2 text-sm text-gray-600">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Processor */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">{isNL ? 'Processor' : 'Processor'}</label>
                  <div className="space-y-2">
                    {[
                      { value: 'intel', label: 'Intel' },
                      { value: 'amd', label: 'AMD' },
                      { value: 'apple', label: 'Apple Silicon' }
                    ].map(processor => (
                      <label key={processor.value} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          checked={selectedProcessors.includes(processor.value)}
                          onChange={(e) => handleFilterChange('processor', processor.value, e.target.checked)}
                        />
                        <span className="ml-2 text-sm text-gray-600">{processor.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* RAM */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">{isNL ? 'Werkgeheugen' : 'RAM'}</label>
                  <div className="space-y-2">
                    {[
                      { value: '8gb', label: '8GB' },
                      { value: '16gb', label: '16GB' },
                      { value: '32gb', label: '32GB' },
                      { value: '64gb', label: '64GB+' }
                    ].map(ram => (
                      <label key={ram.value} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          checked={selectedRam.includes(ram.value)}
                          onChange={(e) => handleFilterChange('ram', ram.value, e.target.checked)}
                        />
                        <span className="ml-2 text-sm text-gray-600">{ram.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Storage Type */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">{isNL ? 'Opslagtype' : 'Storage Type'}</label>
                  <div className="space-y-2">
                    {[
                      { value: 'ssd', label: 'SSD' },
                      { value: 'hdd', label: 'HDD' },
                      { value: 'hybrid', label: 'Hybride' }
                    ].map(storage => (
                      <label key={storage.value} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          checked={selectedStorageType.includes(storage.value)}
                          onChange={(e) => handleFilterChange('storage-type', storage.value, e.target.checked)}
                        />
                        <span className="ml-2 text-sm text-gray-600">{storage.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Storage Capacity */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">{isNL ? 'Opslagcapaciteit' : 'Storage Capacity'}</label>
                  <div className="space-y-2">
                    {[
                      { value: '256gb', label: '256GB' },
                      { value: '512gb', label: '512GB' },
                      { value: '1tb', label: '1TB' },
                      { value: '2tb', label: '2TB+' }
                    ].map(storage => (
                      <label key={storage.value} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          checked={selectedStorageSize.includes(storage.value)}
                          onChange={(e) => handleFilterChange('storage-size', storage.value, e.target.checked)}
                        />
                        <span className="ml-2 text-sm text-gray-600">{storage.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="flex-1">
              {/* Sort and View Controls */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-700">{isNL ? 'Sorteren op:' : 'Sort by:'}</label>
                      <select
                        className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                      >
                        <option value="relevance">{isNL ? 'Relevantie' : 'Relevance'}</option>
                        <option value="name">{isNL ? 'Naam A-Z' : 'Name A-Z'}</option>
                        <option value="rating">{isNL ? 'Klantbeoordeling' : 'Customer Rating'}</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">{isNL ? 'Weergave:' : 'View:'}</span>
                    <button
                      className={`p-2 rounded-md hover:bg-gray-100 cursor-pointer ${viewMode === 'grid' ? 'bg-gray-100' : ''}`}
                      onClick={() => setViewMode('grid')}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-grid-line"></i>
                      </div>
                    </button>
                    <button
                      className={`p-2 rounded-md hover:bg-gray-100 cursor-pointer ${viewMode === 'list' ? 'bg-gray-100' : ''}`}
                      onClick={() => setViewMode('list')}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-list-check"></i>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Products Grid */}
              <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md"
                    data-product-shop
                  >
                    <div className="relative">
                      <div className="h-48 overflow-hidden">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="absolute top-3 left-3">
                        {getStockBadge(product.stock)}
                      </div>
                      <div className="absolute top-3 right-3">
                        <button className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 cursor-pointer">
                          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                            <i className="ri-heart-line"></i>
                          </div>
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          product.type === 'laptop' 
                            ? 'text-teal-600 bg-teal-50' 
                            : 'text-orange-600 bg-orange-50'
                        }`}>
                          {product.type === 'laptop' ? 'Laptop' : 'Desktop'}
                        </span>
                        <div className="flex text-sm">
                          {renderStars(product.rating)}
                          <span className="ml-1 text-gray-500 text-xs">({product.reviewCount})</span>
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold mb-2">{product.name}</h3>
                      <div className="text-sm text-gray-600 mb-3 space-y-1">
                        <div>{product.processor} • {product.ram}</div>
                        <div>{product.storage} • {product.display}</div>
                      </div>
                      <div className="flex space-x-2">
                        <a href={`${prefix}/contact`} className="flex-1 bg-teal-600 text-white px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium hover:bg-teal-700 transition-colors text-center cursor-pointer">
                          {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
                        </a>
                        <button className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 cursor-pointer">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-eye-line"></i>
                          </div>
                        </button>
                        <button className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 cursor-pointer">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-scales-3-line"></i>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredProducts.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center text-gray-400">
                    <i className="ri-search-line ri-2x"></i>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{isNL ? 'Geen producten gevonden' : 'No products found'}</h3>
                  <p className="text-gray-600 mb-4">{isNL ? 'Probeer uw zoek- of filtercriteria aan te passen' : 'Try adjusting your search or filter criteria'}</p>
                  <button 
                    onClick={clearAllFilters}
                    className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer"
                  >
                    {isNL ? 'Alle Filters Wissen' : 'Clear All Filters'}
                  </button>
                </div>
              )}

              {/* Load More */}
              {filteredProducts.length > 0 && (
                <div className="text-center mt-8">
                  <button className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-gray-50 transition-colors cursor-pointer">
                    {isNL ? 'Meer Producten Laden' : 'Load More Products'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default LaptopsDesktopsPage;
